import type { JsonResponse } from '@/types/api/helpers'
import { FileSyncOutlined, LoadingOutlined } from '@ant-design/icons'
import { Dropdown, Menu, Tooltip } from 'antd'
import { useRef, useState } from 'react'
import { fetchClient } from '../../api/typedClient'
import { useObject } from '../../contexts/ObjectContext'
import { UploadStatusModal } from './UploadStatusModal'
import { handleUploadResponse } from './utils/uploadStatus'
import type { UploadStatusNormalized } from './utils/uploadStatus'

interface UploadDownloadButtonsProps {
  refreshCallback?: () => void
}

export function UploadDownloadButtons({
  refreshCallback,
}: UploadDownloadButtonsProps) {
  const hiddenFileInput = useRef<HTMLInputElement>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [uploadStatusModalVisible, setUploadStatusModalVisible] =
    useState(false)
  const [statusResponse, setStatusResponse] = useState<UploadStatusNormalized>()
  const [_selectedMenuKey, setSelectedMenuKey] = useState<number>()

  const { selectedObject } = useObject()

  const handleClick = (input: React.RefObject<HTMLInputElement>) => {
    input.current?.click()
  }

  const handleExport = async () => {
    setIsLoading(true)
    try {
      const { data, error } = await fetchClient.POST(
        '/crossroads/mappings/bulk_download',
        {
          params: {
            query: { type_filter: selectedObject?.value },
          },
          parseAs: 'blob',
        },
      )

      if (error) {
        throw new Error(`HTTP error: ${JSON.stringify(error)}`)
      }

      const blob = data as unknown as Blob

      // Ensure we have a valid blob with content
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty')
      }

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute(
        'download',
        `${selectedObject?.entity_type}_mappings.xlsx`.toLowerCase(),
      )
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Download failed:', error)
      // Show user-friendly error message
      if ((error as Error)?.message === 'Downloaded file is empty') {
        alert(
          'The downloaded file is empty. This might be because there are no mappings to export.',
        )
      } else {
        alert('Download failed. Please try again.')
      }
    } finally {
      setIsLoading(false)
      setSelectedMenuKey(undefined)
    }
  }

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setIsLoading(true)
      setUploadStatusModalVisible(true)
      const file = event.target.files[0]

      if (!file) {
        setIsLoading(false)
        setUploadStatusModalVisible(false)
        return
      }

      const formData = new FormData()
      formData.append('file', file)

      try {
        const { data, error } = await fetchClient.POST(
          '/crossroads/mappings/bulk_upload',
          {
            body: formData as never,
            bodySerializer: (body) => body,
          },
        )

        if (error) {
          throw new Error(`Upload failed: ${JSON.stringify(error)}`)
        }

        handleUploadResponse(
          data as JsonResponse<'/crossroads/mappings/bulk_upload', 'post', 200>,
          setIsLoading,
          setStatusResponse,
          setUploadStatusModalVisible,
          null,
          hiddenFileInput,
        )

        if (refreshCallback) refreshCallback()
        if (hiddenFileInput.current) hiddenFileInput.current.value = ''
        setSelectedMenuKey(undefined)
      } catch (error) {
        console.error('Upload failed:', error)
        setIsLoading(false)
        setUploadStatusModalVisible(false)
        if (hiddenFileInput.current) hiddenFileInput.current.value = ''
        setSelectedMenuKey(undefined)
      }
    }
  }

  const excelDropdownOptions = [
    {
      title: 'Download Template & Data',
      action: 'download',
    },
    {
      title: 'Upload From Excel',
      action: 'upload',
    },
  ]

  const handleOptionClick = (
    option: { title: string; action: string } | undefined,
  ) => {
    if (!option) return
    if (option.action === 'upload') {
      handleClick(hiddenFileInput)
    } else if (option.action === 'download') {
      handleExport()
    }
  }

  const handleMenuClick = (e: { key: string }) => {
    setSelectedMenuKey(parseInt(e.key))
    const optionClicked = excelDropdownOptions[parseInt(e.key)]
    if (!optionClicked) return
    handleOptionClick(optionClicked)
  }

  const menu = (
    <Menu onClick={handleMenuClick}>
      {excelDropdownOptions.map((option, index) => (
        <Menu.Item key={index}>{option.title}</Menu.Item>
      ))}
    </Menu>
  )

  return (
    <>
      <Tooltip title='Work in Excel'>
        <Dropdown.Button
          style={{ width: 'auto' }}
          overlay={menu}
          onClick={() => handleOptionClick(excelDropdownOptions[0])}
          disabled={isLoading}
        >
          {isLoading ? (
            <LoadingOutlined style={{ fontSize: '1.2em' }} />
          ) : (
            <FileSyncOutlined style={{ fontSize: '1.2em' }} />
          )}
        </Dropdown.Button>
      </Tooltip>
      <input
        type='file'
        accept='.xlsx,.xls'
        onChange={handleUpload}
        ref={hiddenFileInput}
        style={{ display: 'none' }}
      />
      <UploadStatusModal
        uploadStatusModalVisible={uploadStatusModalVisible}
        setUploadStatusModalVisible={setUploadStatusModalVisible}
        statusResponse={statusResponse}
        isUploading={isLoading}
        onClose={refreshCallback}
      />
    </>
  )
}
